import React, { useCallback } from 'react';

import { Box, MenuItem, MenuList, useTheme } from '@material-ui/core';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';
import { HiOutlineCog, HiOutlineTrash } from 'react-icons/hi';

import { AppA, AppDataGrid, GridActions, GridCellWithAvatar } from '../../../components';
import { ChatUser, TIME_INTERFACE_PERMISSIONS, TimeMember } from '../../../types';
import { getAvatar } from '../../../utils/getAvatar';
import { GRID_PAGE_SIZE } from '../../../constants';
import { monochrome } from '../../../theme/styles';
import { ChatMessageUserMenuItem } from '../../../components/Chat/ChatMessageUserMenuItem';
import useChatRequests from '../../../hooks/useChatRequests';
import { usePermissions } from '../../../hooks/usePermissions';

interface Props {
  companyId?: string;
  chatCreator?: ChatUser;
  closeGridAction: boolean;
  tableHeight?: string;
  workers: TimeMember[];
  loading: boolean;
  total: number;
  page: number;
  sortModel: GridSortModel;
  removeWorker: (param: TimeMember) => void;
  handleSortModelChange: (newModel: GridSortModel) => void;
  handlePageChange: (newPage: number) => void;
}

export const LocationWorkers: React.FC<Props> = ({
  companyId,
  chatCreator,
  closeGridAction,
  tableHeight = '443px',
  workers,
  loading,
  total,
  page,
  sortModel,
  removeWorker,
  handleSortModelChange,
  handlePageChange,
}) => {
  const history = useHistory();
  const theme = useTheme();
  const { createPrivateChat } = useChatRequests();

  const textColor = theme.palette.type === 'dark' ? monochrome.medium : monochrome.mediumdark;
  const { canOnResource } = usePermissions();

  const canUpdateWorkers = useCallback(
    (siteId?: string) => canOnResource(TIME_INTERFACE_PERMISSIONS.UPDATE_WORKERS, 'location', siteId),
    [canOnResource]
  );

  const canRemoveWorkers = useCallback(
    (siteId?: string) => canOnResource(TIME_INTERFACE_PERMISSIONS.REMOVE_WORKERS, 'location', siteId),
    [canOnResource]
  );

  const goToSettings = (name: string, userId: string) => {
    history.push({
      pathname: `/time/users/${name}_${userId}/profile`,
      state: {
        from: `${history.location.pathname}${history.location.search}`,
        nameOfPrevRoute: 'Locations & Shifts',
      },
    });
  };

  const goToChat = async (timeMember: TimeMember) => {
    if (!chatCreator || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: timeMember.user_id,
        name: `${timeMember.first_name} ${timeMember.last_name}`,
        email: timeMember.email,
        phone: timeMember.phone,
        avatarUrl: timeMember.avatar || timeMember.photo,
      },
      companyId,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Name',
      flex: 0.2,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <GridCellWithAvatar
          link={`/time/users/${params.row.first_name}_${params.row.user_id}/profile`}
          target="_blank"
          avatarUrl={params.row.photo || params.row.avatar}
          avatarPlaceholder={getAvatar(params.row.gender)}
          name={`${params.row.first_name} ${params.row.last_name}`}
        />
      ),
    },
    {
      field: 'ni_number',
      headerName: 'Ref #',
      width: 95,
      flex: 0.2,
      disableColumnMenu: true,
    },
    {
      field: 'site_name',
      headerName: 'Locations',
      width: 95,
      flex: 0.2,
      disableColumnMenu: true,
    },
    {
      field: 'shift_name',
      headerName: 'Shifts',
      width: 95,
      flex: 0.15,
      disableColumnMenu: true,
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      disableColumnMenu: true,
      flex: 0.15,
      minWidth: 180,
      renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
    },
    {
      field: '',
      headerName: 'Actions',
      width: 80,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%">
          <GridActions close={closeGridAction}>
            <MenuList>
              <ChatMessageUserMenuItem
                goToChat={() => goToChat(params.row as TimeMember)}
                textColor={textColor}
              />
              {/* The row's own site_id decides — the same person may manage
                  workers on one location and not on another. */}
              {canUpdateWorkers(params.row.site_id) && (
                <MenuItem onClick={() => goToSettings(params.row.first_name, params.row.user_id)}>
                  <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                    <HiOutlineCog size={14} /> Settings
                  </Box>
                </MenuItem>
              )}
              {canRemoveWorkers(params.row.site_id) && (
                <MenuItem onClick={() => removeWorker(params.row as TimeMember)}>
                  <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                    <HiOutlineTrash size={14} /> Remove from location
                  </Box>
                </MenuItem>
              )}
            </MenuList>
          </GridActions>
        </Box>
      ),
    },
  ];

  return (
    <AppDataGrid
      noPaper
      rows={workers}
      loading={loading}
      height={tableHeight}
      getRowId={row => row.uuid}
      columns={columns}
      rowCount={total}
      page={page}
      paginationMode="server"
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onPageChange={handlePageChange}
      pageSize={GRID_PAGE_SIZE}
      rowsPerPageOptions={[GRID_PAGE_SIZE]}
      disableSelectionOnClick
    />
  );
};
